// Display helpers for the Payment Failures report (PROD-9152); pure, so they are testable.

// Only a bank decline is outside our control — everything else is something we can act on.
export const is_actionable = (category) => category !== 'BANK_DECLINE';

const CATEGORY_LABELS = {
  BANK_DECLINE: 'Bank decline',
  CARD_DATA: 'Card details',
  AUTHENTICATION: 'Verification',
  SYSTEM: 'System',
  CONFIGURATION: 'Integration',
  UNKNOWN: 'Unknown'
};

const STEP_LABELS = {
  PURCHASE_CHARGE: 'Purchase',
  UPGRADE_CHARGE: 'Upgrade',
  RENEWAL_CHARGE: 'Renewal',
  PURCHASE_CREATE_INTENT: 'Purchase — create intent',
  PURCHASE_CONFIRM: 'Purchase — confirm',
  CARD_ADD_CREATE: 'Card add — create',
  CARD_ADD_CONFIRM: 'Card add — confirm',
  CARD_ADD_TOKEN: 'Card add — token',
  IOS_RENEWAL: 'iOS renewal',
  IOS_RECEIPT: 'iOS receipt',
  CLIENT_PAYMENT_CONFIRM: 'Browser — payment',
  CLIENT_CARD_SETUP: 'Browser — card setup',
  CLIENT_SCRIPT_ERROR: 'Browser — script error'
};

const ORIGIN_LABELS = {
  BACKEND: 'Our servers',
  WEBHOOK: 'Stripe',
  CLIENT: 'Browser',
  BACKFILL: 'Historic'
};

const label_from = (map) => (value) => map[value] || value || '—';

export const category_label = label_from(CATEGORY_LABELS);
export const step_label = label_from(STEP_LABELS);
export const origin_label = label_from(ORIGIN_LABELS);

// Origins arrive as a comma-separated list from GROUP_CONCAT.
export const origins_label = (origins) =>
  String(origins || '')
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean)
    .map(origin_label)
    .join(' + ') || '—';

// Historic rows carry no amount, so an empty cell is expected rather than a gap in the data.
export const fmt_amount = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)} ${currency || ''}`.trim();
};

// A student may have no name on file, and a webhook row may not resolve to a student at all —
// fall back to the email, then a marker, so the cell is never blank.
export const student_label = (row) => {
  const name = ((row && row.student_name) || '').trim();
  if (name) return name;
  if (row && row.student_email) return row.student_email;
  return row && row.student_uuid ? 'Unnamed student' : null;
};

// The decline code is the groupable reason; the message is prose and only used as a fallback.
export const reason_label = (row) =>
  (row && (row.error_decline_code || row.error_code)) || (row && row.error_message) || '—';

// Each writer gets its own colour so a row's provenance reads at a glance.
const ORIGIN_TONES = {
  BACKEND: 'info',
  WEBHOOK: 'warning',
  CLIENT: 'success',
  BACKFILL: 'muted'
};

export const origin_tone = (origin) => ORIGIN_TONES[origin] || 'muted';

// A bank decline is nobody's bug; everything else is something we can act on, so it stands out.
export const category_tone = (category) => {
  if (!category || category === 'UNKNOWN') return 'muted';
  return category === 'BANK_DECLINE' ? 'neutral' : 'error';
};

// Maps a tone to theme tokens — never a hardcoded colour, so dark mode follows.
const TONE_TOKENS = {
  info: { bg: 'var(--state-info-bg)', text: 'var(--state-info-text)' },
  warning: { bg: 'var(--state-warning-bg)', text: 'var(--state-warning-text)' },
  success: { bg: 'var(--state-success-bg)', text: 'var(--state-success-text)' },
  error: { bg: 'var(--state-error-bg)', text: 'var(--state-error-text)' },
  neutral: { bg: 'var(--surface-sunken)', text: 'var(--text-secondary)' },
  muted: { bg: 'var(--surface-sunken)', text: 'var(--text-muted)' }
};

export const tone_style = (tone) => {
  const t = TONE_TOKENS[tone] || TONE_TOKENS.muted;
  return {
    background: t.bg,
    color: t.text,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 12,
    whiteSpace: 'nowrap'
  };
};
